<script>
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    selectedClassification: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:show', 'select'],
  data() {
    return {
      searchTerm: '',
    };
  },
  computed: {
    ...mapGetters({
      childrenClassifications: 'classifications/getChildrenClassifications',
    }),
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    filteredClassifications() {
      if (!this.searchTerm) {
        return this.availableClassifications;
      }

      return this.availableClassifications.filter(classification =>
        classification.friendly_name
          .toLowerCase()
          .includes(this.searchTerm.toLowerCase())
      );
    },
    availableClassifications() {
      return this.childrenClassifications.filter(
        classification => classification.active
      );
    },
    hasResults() {
      return this.filteredClassifications.length > 0;
    },
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.focusSearchInput();
      }
    },
  },
  mounted() {
    this.$store.dispatch('classifications/get');
  },
  methods: {
    onClose() {
      this.localShow = false;
      this.searchTerm = '';
    },
    onSelectClassification(classification) {
      this.$emit('select', classification);
      this.onClose();
    },
    onRemoveClassification() {
      this.$emit('select', 0);
      this.onClose();
    },
    focusSearchInput() {
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },
    isSelected(classification) {
      return (
        this.selectedClassification &&
        this.selectedClassification.id === classification.id
      );
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose" size="medium">
    <woot-modal-header
      :header-title="
        $t('CONVERSATION.CLASSIFICATION.CHANGE_CLASSIFICATION.TITLE')
      "
      :header-content="
        $t('CONVERSATION.CLASSIFICATION.CHANGE_CLASSIFICATION.DESCRIPTION')
      "
    />

    <div class="flex flex-col px-8 pb-8 pt-4">
      <!-- Search input -->
      <div class="mb-4">
        <div class="relative">
          <input
            ref="searchInput"
            v-model="searchTerm"
            type="text"
            class="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            :placeholder="
              $t(
                'CONVERSATION.CLASSIFICATION.CHANGE_CLASSIFICATION.SEARCH_PLACEHOLDER'
              )
            "
          />
        </div>
      </div>

      <!-- Remove classification button -->
      <div v-if="selectedClassification" class="mb-4">
        <NextButton
          variant="outline"
          slate
          class="w-full !justify-start !px-3"
          icon="i-lucide-x"
          @click="onRemoveClassification"
        >
          <span class="text-sm">
            {{
              $t(
                'CONVERSATION.CLASSIFICATION.CHANGE_CLASSIFICATION.REMOVE_CLASSIFICATION'
              )
            }}
          </span>
        </NextButton>
      </div>

      <!-- Classification list -->
      <div class="max-h-[60vh] overflow-y-auto">
        <div v-if="hasResults" class="space-y-1">
          <NextButton
            v-for="classification in filteredClassifications"
            :key="classification.id"
            variant="ghost"
            slate
            class="w-full !justify-between !px-3"
            :class="{
              '!bg-slate-100 dark:!bg-slate-700': isSelected(classification),
            }"
            @click="onSelectClassification(classification)"
          >
            <span class="text-sm text-left truncate">
              {{ classification.friendly_name }}
            </span>
            <i
              v-if="isSelected(classification)"
              class="icon i-lucide-check text-blue-600 dark:text-blue-400"
            />
          </NextButton>
        </div>

        <!-- Empty state -->
        <div v-else class="text-center py-8">
          <p class="text-slate-500 dark:text-slate-400 text-sm">
            <span v-if="searchTerm">
              {{
                $t(
                  'CONVERSATION.CLASSIFICATION.CHANGE_CLASSIFICATION.NO_RESULTS'
                )
              }}
            </span>
            <span v-else>
              {{
                $t(
                  'CONVERSATION.CLASSIFICATION.CHANGE_CLASSIFICATION.NO_CLASSIFICATIONS'
                )
              }}
            </span>
          </p>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<style scoped>
.icon {
  width: 16px;
  height: 16px;
}
</style>
